/**
 * Lo que solo sabe el servidor, para la pantalla de inicio del dueño.
 *
 * A phone that has stopped syncing cannot say so itself — it is the one not calling — and an
 * event the server refused shows on the device that sent it, not on the owner's. So the owner's
 * home screen asks the server: which devices have gone quiet, and how many refusals nobody has
 * reviewed yet.
 */

import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { getMeta } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { api } from '../sync/client.js';
import { enPractica } from './practica.js';

export interface EquipoVisto {
  id: string;
  activo: boolean;
  ultimoVistoEn: string | null;
}

/**
 * How long a device may go without reaching the server before the owner hears of it: a day at the
 * least (the owner, 2026-10-13: «should really be warning me only if a device hasn't connected in
 * one or two days»). A phone switched off for the night, or a spare one in a drawer, has not
 * stopped syncing. The setting went down to four hours until then, and the live shop had it there:
 * anything under a day picked then reads as a day.
 */
export const horasDeAviso = (horas: number): number => Math.max(24, horas);

/**
 * Devices still in service, other than this one, that have not reached the server in more than
 * `horas`, a day at the least. One that has never connected is left out: it has never synced
 * anything to hold back, and registering it was a connection the moment before.
 */
export function equiposCallados<T extends EquipoVisto>(
  equipos: readonly T[],
  horas: number,
  propio: string | null,
  ahora: number,
): T[] {
  const limite = ahora - horasDeAviso(horas) * 3_600_000;
  return equipos.filter(
    (d) => d.activo && d.id !== propio && d.ultimoVistoEn !== null && Date.parse(d.ultimoVistoEn) < limite,
  );
}

/**
 * Whether «Equipos» shows a device in service: the server's word once it has answered (it is the
 * server that takes a device's syncs or turns them away); until then what this phone has heard,
 * and that too while this phone holds a revocation or a giving back of its own not yet sent, so
 * either shows at once. Both used to have to say «in service», and a phone that let a giving back
 * go by, as an older version drops an event it does not know, showed a working device revoked
 * for good.
 */
export const enServicio = (servidor: boolean | null, local: boolean | undefined, sinEnviar: boolean): boolean =>
  servidor !== null && !sinEnviar ? servidor : (local ?? servidor ?? true);

/** A clock off by more than the shop allows (`relojDerivaMaxMs`): its sales get the wrong time. */
export const relojDesviado = (derivaMs: number | null | undefined, maxMs: number): derivaMs is number =>
  derivaMs !== null && derivaMs !== undefined && Math.abs(derivaMs) > maxMs;

const CADA_MS = 5 * 60_000;

export interface AvisosDelServidor {
  callados: number;
  rechazos: number;
}

/**
 * Asked when the home screen opens and every five minutes after. With no signal the last answer
 * stays — never an all-clear it has not heard — and the practice shop, with no server, asks
 * nothing.
 */
export function useAvisosDelServidor(puede: { equipos: boolean; rechazos: boolean }): AvisosDelServidor | null {
  const horas = useLiveQuery(async () => (await readSettings()).equipoCalladoHoras, []);
  const [avisos, setAvisos] = useState<AvisosDelServidor | null>(null);
  const { equipos, rechazos } = puede;

  useEffect(() => {
    if (enPractica() || horas === undefined || (!equipos && !rechazos)) return;
    let vivo = true;
    async function pedir() {
      try {
        const [lista, refusals] = await Promise.all([
          equipos ? api.dispositivos() : null,
          rechazos ? api.rechazos(false) : null,
        ]);
        const propio = (await getMeta('dispositivoId')) ?? null;
        if (!vivo) return;
        setAvisos({
          callados: lista ? equiposCallados(lista.dispositivos, horas!, propio, Date.now()).length : 0,
          rechazos: refusals?.rechazos.length ?? 0,
        });
      } catch {
        // No signal, or the server said no: keep what was last heard.
      }
    }
    void pedir();
    const reloj = window.setInterval(() => void pedir(), CADA_MS);
    return () => {
      vivo = false;
      window.clearInterval(reloj);
    };
  }, [equipos, rechazos, horas]);

  return avisos;
}
